import { clinicClient } from '@/common/apis/client';
import { formData } from '@/common/utils/formData';
import { useMutation, useQuery } from '@tanstack/react-query';
import { setTerminal } from '../auth/setTerminal';
import { growthbook } from 'src/pages/_app';

interface Params {
  book_id: string;
  type?: 'factor';
}

// Last POST /api/getBooks attempt, for the price_not_loaded payload (price source diagnostics).
// Module-level on purpose: one shared request serves page + invoice, so one tracker is enough.
export const getBooksTelemetry: {
  durationMs?: number;
  httpStatus?: number;
  error?: string;
  attempts: number;
} = { attempts: 0 };

export const getBookDetails = (params: Params) => {
  setTerminal();
  const endpoints = growthbook.getFeatureValue<Record<string, string>>('booking:api-endpoints', {});
  const startedAt = Date.now();
  getBooksTelemetry.attempts += 1;

  return clinicClient
    .post(
      endpoints?.get_books ?? '/api/getBooks',
      formData({
        ...params,
      }),
    )
    .then(res => {
      getBooksTelemetry.durationMs = Date.now() - startedAt;
      getBooksTelemetry.httpStatus = res?.status;
      getBooksTelemetry.error = undefined;
      return res;
    })
    .catch(error => {
      getBooksTelemetry.durationMs = Date.now() - startedAt;
      getBooksTelemetry.httpStatus = error?.response?.status;
      getBooksTelemetry.error = error?.response?.data?.message ?? error?.message;
      throw error;
    });
};

export const useGetBookDetails = () => {
  return useMutation(getBookDetails);
};

// Shared, deduped cache entry for the factor book row: the page renders doctor info and
// useInvoice reads the price from this same query — one POST /api/getBooks per book.
export const useBookDetailsQuery = ({ bookId, enabled = true }: { bookId?: string; enabled?: boolean }) => {
  return useQuery(
    ['bookFactorDetails', bookId],
    () => getBookDetails({ book_id: bookId!.toString(), type: 'factor' }),
    {
      enabled: enabled && !!bookId,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 4000),
    },
  );
};
